/**
 * Central API bootstrap: base URL, auth headers, and 401 refresh — runs once on import.
 * All existing `import axios from 'axios'` calls use this configured default instance.
 */
import axios from 'axios';

const baseURL = process.env.REACT_APP_DOMAIN_URL_KEY;
axios.defaults.baseURL = baseURL;

/**
 * No auth interceptors — used only for POST Account/GetToken so refresh cannot recurse.
 */
export const refreshClient = axios.create({
  baseURL,
});

let isRefreshing = false;
/** @type {Array<(err: unknown, token: string | null) => void>} */
let refreshSubscribers = [];

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

function notifyRefreshSubscribers(error, token) {
  refreshSubscribers.forEach((cb) => {
    try {
      cb(error, token);
    } catch (e) {
      console.error(e);
    }
  });
  refreshSubscribers = [];
}

function isGetTokenRequest(config) {
  const u = String(config?.url || '');
  return u.includes('Account/GetToken');
}

axios.interceptors.request.use(
  (request) => {
    const access_token = sessionStorage.getItem('access_token');
    request.headers.Authorization = `Bearer ${access_token}`;
    return request;
  },
  (error) => {
    console.log(error);
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status !== 401) {
      return Promise.reject(error);
    }

    if (!originalRequest || isGetTokenRequest(originalRequest)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((err, token) => {
          if (err) {
            reject(err);
            return;
          }
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axios(originalRequest));
        });
      });
    }

    isRefreshing = true;
    originalRequest._retry = true;

    try {
      const refresh_token = sessionStorage.getItem('refresh_token');
      const access_token = sessionStorage.getItem('access_token');

      const response = await refreshClient.post('Account/GetToken', {
        access_token,
        refresh_token,
        grant_type: 'refresh_token',
      });

      if (response.status === 200) {
        sessionStorage.setItem('refresh_token', response?.data?.refresh_token);

        if (response?.data?.access_token) {
          sessionStorage.setItem('access_token', response.data.access_token);
        }

        const newAccess = response?.data?.access_token;
        axios.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
        notifyRefreshSubscribers(null, newAccess);

        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return axios(originalRequest);
      }

      notifyRefreshSubscribers(error, null);
      return Promise.reject(error);
    } catch (refreshError) {
      console.error('Error refreshing access token:', refreshError);
      notifyRefreshSubscribers(refreshError, null);
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

export { axios as api };
export default axios;
